import { useState, useMemo, useEffect, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useForm, useFieldArray, useWatch, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight, Plus, Wrench, Clock, CheckCircle, XCircle, Edit2, RotateCcw, Trash2, Paperclip, Flag, CalendarClock, ExternalLink } from "lucide-react";
import DOMPurify from "dompurify";
import apiClient from "@/api/client";
import {
  MANAGED_FILE_MAX_BYTES,
  fileTooLargeMessage,
  findTooLargeFile,
  uploadErrorMessage,
  uploadManagedFile,
} from "@/utils/managedFile";
import {
  useRequisition,
  useSubmitRequisition,
  useApproveRequisition,
  useReopenRequisition,
  useUpdateRequisition,
  useDeleteRequisition,
  useDeleteRequisitionAttachment,
  useRequisitionAuditLogs,
  useAddRequisitionAuditLog,
} from "../hooks/useRequisitions";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { Select } from "@/components/ui/Select";
import { DatePicker } from "@/components/ui/DatePicker";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { PageLoader } from "@/components/ui/Spinner";
import { useAuthStore } from "@/store/auth";
import { MANUAL_ORDER_TYPE_OPTIONS, PRIORITY_LABELS, REQUISITION_STATUS_LABELS, type RequisitionStatus, type ManagedFileBrief } from "../types";
import { LineItemFormRow, lineItemSchema } from "../components/LineItemFormRow";
import { MilestonesEditor, latestMilestoneDate, DEADLINE_BEFORE_MILESTONE_MSG, type MilestoneDraft } from "../components/MilestonesEditor";
import { MinOrderAmountNotice } from "../components/MinOrderAmountNotice";
import { RequisitionAutoLineItems, toggleableAutoRows } from "../components/RequisitionAutoLineItems";
import { RequisitionRejectModal } from "../components/RequisitionRejectModal";
import { computeMinOrderNotice } from "../utils/minOrderAmount";
import {
  emptyProjectFileShare, ProjectFileShareFields, projectFileShareFields, projectFileShareValid,
} from "@/features/power_projects/components/ProjectFileShareFields";
import type { ProjectFileShareValue } from "@/features/power_projects/components/ProjectFileShareFields";
import { AttachmentList } from "../components/AttachmentList";
import { OrderResultFilesSection } from "../components/OrderResultFilesSection";
import { usePageTitle } from "@/hooks/usePageTitle";
import { RichTextEditor } from "@/components/ui/RichTextEditor";
import { MentionTextarea } from "@/components/ui/MentionTextarea";
import { highlightMentions } from "@/utils/highlightMentions";
import { formatDate } from "@/utils/formatDate";
import { FileDropZone } from "@/components/ui/FileDropZone";

// --- Types ---

interface Project { id: string; name: string; short_name: string; is_active: boolean; }
interface ContractorOption { id: string; name: string; short_name: string; is_active: boolean; }
interface LineItemType { id: string; name: string; unit: string; base_rate: string; high_priority_rate?: string | null; attachment_required: boolean; }
interface Agreement { id: string; title: string; min_order_amount?: string | null; line_item_types: LineItemType[]; }

// --- Edit form schema ---

const editSchema = z.object({
  project: z.string().min(1, "Wybierz przedsięwzięcie"),
  contractor: z.string().optional(),
  description: z
    .string()
    .min(1, "Opis jest wymagany")
    .refine(
      (val) => val.replace(/<[^>]*>/g, "").trim().length >= 10,
      "Opis musi mieć co najmniej 10 znaków"
    ),
  priority: z.enum(["low", "standard", "high"]),
  deadline: z.string().optional(),
  line_items: z.array(lineItemSchema).min(1, "Dodaj co najmniej jedną pozycję realizacyjną"),
});

type EditFormData = z.infer<typeof editSchema>;

const statusColors: Record<RequisitionStatus, "gray" | "blue" | "green" | "red"> = {
  draft: "gray", submitted: "blue", approved: "green", rejected: "red",
};

// Legacy requisitions store plain-text descriptions; wrap them into paragraphs so
// the rich text editor doesn't collapse newlines when parsing the value as HTML
function toEditorHtml(text: string): string {
  if (!text || text.trimStart().startsWith("<")) return text;
  const escape = (s: string) =>
    s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return text.split(/\r?\n/).map((line) => `<p>${escape(line)}</p>`).join("");
}

const uploadFile = (file: File) => uploadManagedFile(file);

// --- Queries for edit form ---

function useProjects() {
  return useQuery({
    queryKey: ["projects"],
    queryFn: () => apiClient.get<{ data: Project[] }>("/projects/").then((r) => r.data.data),
  });
}

function useContractors() {
  return useQuery({
    queryKey: ["contractors", "active"],
    queryFn: () =>
      apiClient.get<{ data: ContractorOption[] }>("/contractors/", { params: { is_active: "true" } })
        .then((r) => r.data.data),
  });
}

function useContractorAgreements(contractorId: string | undefined) {
  return useQuery({
    queryKey: ["contractor-agreements", contractorId],
    queryFn: () =>
      apiClient.get<{ data: Agreement[] }>(`/contractors/${contractorId}/agreements/`)
        .then((r) => r.data.data),
    enabled: !!contractorId,
  });
}

// --- Main component ---

export function RequisitionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();

  const isDirector = user?.role === "director";
  // Przejście „zapotrzebowanie → projekt ZE". Bramkowane tak samo jak na zamówieniu
  // (OrderDetailPage) i jak trasa /power-projects: bez modułu DLC_01 link odbiłby
  // użytkownika na stronę główną.
  const canOpenZeProject = Boolean(
    user && user.role !== "contractor" && user.accessible_dlc_modules?.includes("dlc_01"),
  );
  const canApprove = user?.has_orderer_permission;
  const canReject = user?.has_orderer_permission;
  const canDelete = user?.is_ceo || (user?.role === "director" && user?.is_technical_director);

  const { data: requisition, isLoading } = useRequisition(id!);
  usePageTitle(requisition && `Zapotrzebowanie: ${requisition.project_name}`);
  const { data: auditLogs } = useRequisitionAuditLogs(id!);

  const submitMutation = useSubmitRequisition();
  const approveMutation = useApproveRequisition();
  const reopenMutation = useReopenRequisition();
  const updateMutation = useUpdateRequisition();
  const deleteMutation = useDeleteRequisition();
  const deleteAttachmentMutation = useDeleteRequisitionAttachment();
  const addLogMutation = useAddRequisitionAuditLog(id!);

  // Edit mode state
  const [editMode, setEditMode] = useState(false);
  const [newAttachments, setNewAttachments] = useState<File[]>([]);
  const [deletingAttachmentId, setDeletingAttachmentId] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  // Approve modal
  const [approveModal, setApproveModal] = useState(false);
  const [approveDeadline, setApproveDeadline] = useState("");
  const [approveOrderType, setApproveOrderType] = useState("standard");
  const [approveMilestones, setApproveMilestones] = useState<MilestoneDraft[]>([]);
  // Udostępnienie zakładki „Pliki" projektu ZE w tworzonym zamówieniu — ta sama opcja,
  // co przy „Zleć podwykonawcy" z projektu technicznego (zamówienia z zapotrzebowania
  // dotąd jej nie miały i komplet plików trzeba było klikać folder po folderze).
  const [approveShare, setApproveShare] = useState<ProjectFileShareValue>(emptyProjectFileShare);
  // Pozycje automatyczne umowy, których zatwierdzający NIE chce na zamówieniu (start: lista
  // pominiętych z zapotrzebowania) → overrides.declined_auto_line_item_types.
  const [approveDeclinedAuto, setApproveDeclinedAuto] = useState<Set<string>>(new Set());
  const [editMilestones, setEditMilestones] = useState<MilestoneDraft[]>([]);
  // Reject modal (RequisitionRejectModal — shared with the director's „Do zatwierdzenia")
  const [rejectModal, setRejectModal] = useState(false);
  // Delete modal
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  // Add note modal
  const [noteModal, setNoteModal] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [noteMentions, setNoteMentions] = useState<string[]>([]);

  const handleImageUpload = useCallback(async (file: File): Promise<string> => {
    const result = await uploadFile(file);
    return result.url ?? "";
  }, []);

  // --- Edit form ---
  const { data: projects } = useProjects();
  const { data: contractors } = useContractors();

  const { register, control, handleSubmit, setValue, reset, formState: { errors } } =
    useForm<EditFormData>({
      resolver: zodResolver(editSchema),
      defaultValues: { priority: "standard", line_items: [] },
    });

  const { fields, append, remove } = useFieldArray({ control, name: "line_items" });
  const watchedContractor = useWatch({ control, name: "contractor" });
  const watchedDeadline = useWatch({ control, name: "deadline" });
  const watchedLineItems = useWatch({ control, name: "line_items" });
  const watchedPriority = useWatch({ control, name: "priority" });
  // Fall back to requisition's contractor so agreements pre-load before edit mode opens
  const contractorId = watchedContractor || requisition?.contractor || undefined;
  const { data: agreements } = useContractorAgreements(contractorId);

  // Edycja: to samo ostrzeżenie o kwocie minimalnej co w formularzu nowego
  // zapotrzebowania, liczone na żywo z wpisywanych pozycji (w podglądzie i w oknie
  // zatwierdzania pokazujemy wersję policzoną przez backend).
  const editMinOrderNotice = useMemo(
    () => (editMode && watchedContractor ? computeMinOrderNotice(agreements, watchedLineItems, watchedPriority) : null),
    [editMode, watchedContractor, agreements, watchedLineItems, watchedPriority],
  );

  const lineItemOptions = useMemo<{ value: string; label: string }[]>(() => {
    if (!agreements) return [];
    return agreements.flatMap((a) =>
      a.line_item_types.map((lit) => ({
        value: lit.id,
        label: `${lit.name} — ${lit.unit}${lit.base_rate != null ? ` @ ${parseFloat(lit.base_rate).toLocaleString("pl-PL", { useGrouping: "always" })} zł` : ""}`,
      }))
    );
  }, [agreements]);

  const lineItemAttachmentMap = useMemo<Record<string, boolean>>(() => {
    if (!agreements) return {};
    return Object.fromEntries(
      agreements.flatMap((a) => a.line_item_types.map((lit) => [lit.id, lit.attachment_required]))
    );
  }, [agreements]);

  const projectOptions = useMemo(
    () => (projects ?? []).filter((p) => p.is_active).map((p) => ({ value: p.id, label: `${p.short_name} — ${p.name}` })),
    [projects]
  );
  const contractorOptions = useMemo(
    () => (contractors ?? []).map((c) => ({ value: c.id, label: `${c.name} (${c.short_name})` })),
    [contractors]
  );

  // Populate edit form when requisition loads or edit mode is entered
  useEffect(() => {
    if (editMode && requisition) {
      reset({
        project: requisition.project,
        contractor: requisition.contractor ?? "",
        description: toEditorHtml(requisition.description),
        priority: requisition.priority,
        deadline: requisition.deadline ?? "",
        line_items: requisition.line_items.map((li) => {
          if (li.is_custom) {
            return {
              is_custom: true,
              custom_name: li.custom_name ?? "",
              custom_unit: li.custom_unit ?? "",
              custom_unit_rate: li.custom_unit_rate ? parseFloat(li.custom_unit_rate) : undefined,
              quantity: parseFloat(li.quantity),
              description: li.description ?? "",
              attachment_required: li.attachment_required,
              required_file_extension: li.required_file_extension ?? "",
              result_files_expected: li.result_files_expected,
            };
          }
          return {
            is_custom: false,
            line_item_type: li.line_item_type ?? "",
            quantity: parseFloat(li.quantity),
            description: li.description ?? "",
            attachment_required: li.attachment_required,
            required_file_extension: li.required_file_extension ?? "",
            result_files_expected: li.result_files_expected,
          };
        }),
      });
      setEditMilestones(
        (requisition.milestones ?? []).map((m) => ({ label: m.label, due_date: m.due_date })),
      );
    }
  }, [editMode, requisition, reset]);

  const onSave = async (data: EditFormData) => {
    const latestMs = latestMilestoneDate(editMilestones);
    if (data.deadline && latestMs && data.deadline < latestMs) return;
    let extraFileIds: string[] = [];
    setUploadError(null);
    if (newAttachments.length > 0) {
      setIsUploading(true);
      try {
        const uploaded = await Promise.all(newAttachments.map(uploadFile));
        extraFileIds = uploaded.map((f) => f.id);
      } catch (err) {
        // Without this the rejection escaped the submit handler: the button went back
        // to „Zapisz zmiany" and nothing said why (2026-10-20).
        setUploadError(uploadErrorMessage(err, "Nie udało się wgrać załączników — spróbuj ponownie."));
        return;
      } finally {
        setIsUploading(false);
      }
    }

    const existingFileIds = (requisition?.files_detail ?? []).map((f) => f.id);
    const files = extraFileIds.length > 0
      ? [...existingFileIds, ...extraFileIds]
      : undefined;

    const line_items = data.line_items.map((item) => {
      if (item.is_custom) {
        return {
          line_item_type: null,
          custom_name: item.custom_name,
          custom_unit: item.custom_unit,
          custom_unit_rate: item.custom_unit_rate,
          quantity: item.quantity,
          description: item.description,
          attachment_required: item.attachment_required,
          required_file_extension: item.required_file_extension,
          result_files_expected: item.result_files_expected,
        };
      }
      return {
        line_item_type: item.line_item_type,
        quantity: item.quantity,
        description: item.description,
        attachment_required: item.attachment_required,
        required_file_extension: item.required_file_extension,
        result_files_expected: item.result_files_expected,
      };
    });
    const milestones = editMilestones
      .filter((m) => m.label.trim() && m.due_date)
      .map((m) => ({ label: m.label.trim(), due_date: m.due_date }));
    updateMutation.mutate(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      { id: id!, data: { ...data, deadline: data.deadline || null, line_items, milestones, ...(files ? { files } : {}) } as any },
      { onSuccess: () => { setEditMode(false); setNewAttachments([]); } }
    );
  };

  const approveLatestMs = latestMilestoneDate(approveMilestones);
  const approveDeadlineTooEarly = !!approveDeadline && !!approveLatestMs && approveDeadline < approveLatestMs;
  const editLatestMs = latestMilestoneDate(editMilestones);
  const editDeadlineTooEarly = !!watchedDeadline && !!editLatestMs && watchedDeadline < editLatestMs;

  const handleApproveConfirm = () => {
    if (approveDeadlineTooEarly) return;
    const overrides: Record<string, unknown> = {};
    if (approveDeadline) overrides.deadline = approveDeadline;
    if (approveOrderType && approveOrderType !== "standard") overrides.order_type = approveOrderType;
    // Always send the (approver-editable) milestones so they carry to the order — an empty
    // list intentionally drops the requisition's proposed checkpoints.
    overrides.milestones = approveMilestones
      .filter((m) => m.label.trim() && m.due_date)
      .map((m) => ({ label: m.label.trim(), due_date: m.due_date }));
    // Tylko gdy było co przełączać — lista zastępuje pominięcia zapisane na zapotrzebowaniu.
    if (toggleableAutoRows(requisition?.auto_line_items).length > 0) {
      overrides.declined_auto_line_item_types = [...approveDeclinedAuto];
    }
    approveMutation.mutate(
      {
        id: requisition!.id,
        overrides,
        // Pola udostępnienia lecą tylko dla przedsięwzięcia z projektem ZE — backend i tak
        // odrzuca prośbę bez niego, ale nie ma po co go pytać.
        extra: requisition?.ze_project_id ? projectFileShareFields(approveShare) : undefined,
      },
      {
        onSuccess: () => {
          setApproveModal(false);
          setApproveDeadline("");
          setApproveOrderType("standard");
          setApproveMilestones([]);
          setApproveShare(emptyProjectFileShare());
        },
      },
    );
  };

  const handleAddNote = () => {
    if (!noteText.trim()) return;
    addLogMutation.mutate(
      { description: noteText.trim(), mentionIds: noteMentions },
      {
        onSuccess: () => { setNoteModal(false); setNoteText(""); setNoteMentions([]); },
      },
    );
  };

  const handleDeleteAttachment = async (file: ManagedFileBrief) => {
    if (!window.confirm(`Usunąć załącznik „${file.original_filename}”?`)) return;
    setDeletingAttachmentId(file.id);
    try {
      await deleteAttachmentMutation.mutateAsync({ id: id!, fileId: file.id });
    } catch {
      window.alert("Nie udało się usunąć załącznika.");
    } finally {
      setDeletingAttachmentId(null);
    }
  };

  if (isLoading) return <PageLoader />;
  if (!requisition) return <p className="text-gray-500 dark:text-gray-400">Nie znaleziono zapotrzebowania.</p>;

  const isReporter = user?.id === requisition.reporter;
  // Reporter can edit their own non-approved requisitions (including rejected — to fix and resubmit); director can edit any non-approved
  const canEditNow =
    requisition.status !== "approved" &&
    (isDirector || isReporter || requisition.status === "draft");

  return (
    <div className="max-w-3xl">
      {/* Back */}
      <Link
        to="/requisitions"
        className="inline-flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-800 mb-5"
      >
        <ArrowLeft size={14} /> Powrót do listy
      </Link>

      {/* Header */}
      <div className="mb-5">
        {/* Nazwa przedsięwzięcia jest przejściem do workspace'u ZE — tak samo jak na
            zamówieniu. Trasa kluczuje po id PRZEDSIĘWZIĘCIA, nie po id profilu ZE. */}
        <h1 className="text-sm font-bold text-gray-900 dark:text-gray-100 sm:text-lg leading-snug">
          {requisition.has_ze_project && canOpenZeProject ? (
            <Link
              to={`/power-projects/${requisition.project}`}
              title="Przejdź do projektu ZE"
              className="hover:text-[var(--color-primary)] hover:underline inline-flex items-center gap-1.5"
            >
              {requisition.project_name}
              <ExternalLink size={14} className="shrink-0" />
            </Link>
          ) : (
            requisition.project_name
          )}
        </h1>
        <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1 mb-2" />
        <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
          <Badge color={requisition.priority === "high" ? "red" : requisition.priority === "low" ? "gray" : "blue"}>
            {PRIORITY_LABELS[requisition.priority]}
          </Badge>
          <Badge color={statusColors[requisition.status]}>
            {REQUISITION_STATUS_LABELS[requisition.status]}
          </Badge>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Zgłoszone przez: {requisition.reporter_name} ·{" "}
          {new Date(requisition.created_at).toLocaleDateString("pl")}
        </p>
      </div>

      {/* Action bar */}
      {!editMode && (
        <div className="flex flex-wrap items-center gap-2 mb-6">
          {/* Approved requisition → jump straight to the order it generated. Requisitions and
              orders sit behind the same route guard, so anyone seeing this page can open it. */}
          {requisition.order_id && (
            <Link to={`/orders/${requisition.order_id}`}>
              <Button size="sm">
                Przejdź do zamówienia
                {requisition.order_number && ` ${requisition.order_number}`}
                <ArrowRight size={14} />
              </Button>
            </Link>
          )}
          {canEditNow && (
            <Button size="sm" variant="secondary" onClick={() => setEditMode(true)}>
              <Edit2 size={14} /> Edytuj
            </Button>
          )}
          {requisition.status === "draft" && (
            <div className="flex flex-col gap-1">
              <Button
                size="sm"
                loading={submitMutation.isPending}
                onClick={() => submitMutation.mutate(requisition.id)}
              >
                Zgłoś do zatwierdzenia
              </Button>
              {submitMutation.isError && (
                <p className="text-xs text-red-600">
                  {(submitMutation.error as { response?: { data?: { errors?: { detail?: string } } } })
                    ?.response?.data?.errors?.detail ?? "Błąd zgłoszenia. Spróbuj ponownie."}
                </p>
              )}
            </div>
          )}
          {requisition.status === "submitted" && canApprove && (
            <Button
              size="sm"
              loading={approveMutation.isPending}
              onClick={() => {
                setApproveDeadline(requisition.deadline ?? "");
                setApproveOrderType(requisition.suggested_order_type ?? "standard");
                setApproveMilestones(
                  (requisition.milestones ?? []).map((m) => ({ label: m.label, due_date: m.due_date })),
                );
                setApproveDeclinedAuto(new Set(requisition.declined_auto_line_item_types ?? []));
                setApproveModal(true);
              }}
            >
              <CheckCircle size={14} /> Zatwierdź
            </Button>
          )}
          {requisition.status === "submitted" && canReject && (
            <Button
              size="sm"
              variant="danger"
              onClick={() => setRejectModal(true)}
            >
              <XCircle size={14} /> Odrzuć
            </Button>
          )}
          {requisition.status === "rejected" && canReject && (
            <div className="flex flex-col gap-1">
              <Button
                size="sm"
                variant="secondary"
                loading={reopenMutation.isPending}
                onClick={() => reopenMutation.mutate(requisition.id)}
              >
                <RotateCcw size={14} /> Cofnij odrzucenie
              </Button>
              {reopenMutation.isError && (
                <p className="text-xs text-red-600">Błąd cofania odrzucenia. Spróbuj ponownie.</p>
              )}
            </div>
          )}
          {canDelete && !requisition.order_id && (
            <Button size="sm" variant="danger" onClick={() => setDeleteConfirmOpen(true)}>
              <Trash2 size={14} /> Usuń
            </Button>
          )}
        </div>
      )}

      {/* View mode */}
      {!editMode && (
        <div className="flex flex-col gap-5">
          <Section label="Opis">
            {requisition.description.trimStart().startsWith("<") ? (
              <div
                className="rich-text-content text-sm text-gray-700 dark:text-gray-300"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(requisition.description, {
                    ALLOWED_TAGS: ["p", "br", "strong", "em", "u", "ul", "ol", "li", "img", "h1", "h2", "h3"],
                    ALLOWED_ATTR: ["src", "alt", "width", "height", "start"],
                  }),
                }}
              />
            ) : (
              <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{requisition.description}</p>
            )}
          </Section>

          <div className="grid grid-cols-2 gap-3">
            <InfoCard label="Podwykonawca" value={requisition.contractor_name ?? "— nie wskazano —"} />
            <InfoCard label="Termin realizacji" value={formatDate(requisition.deadline)} />
          </div>

          {/* Line items */}
          <Section label="Pozycje realizacyjne">
            <table className="w-full text-xs bg-white">
              <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className="text-left px-3 py-2.5 text-xs uppercase tracking-widest font-semibold text-gray-500 dark:text-gray-400">Pozycja</th>
                  <th className="text-right px-3 py-2.5 text-xs uppercase tracking-widest font-semibold text-gray-500 dark:text-gray-400">Ilość</th>
                  <th className="text-right px-3 py-2.5 text-xs uppercase tracking-widest font-semibold text-gray-500 dark:text-gray-400">Stawka</th>
                  <th className="text-right px-3 py-2.5 text-xs uppercase tracking-widest font-semibold text-gray-500 dark:text-gray-400">Wartość</th>
                  <th className="hidden sm:table-cell text-left px-3 py-2.5 text-xs uppercase tracking-widest font-semibold text-gray-500 dark:text-gray-400">Opis</th>
                  <th className="hidden sm:table-cell text-center px-3 py-2.5 text-xs uppercase tracking-widest font-semibold text-gray-500 dark:text-gray-400">Załącznik</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {requisition.line_items.map((li) => (
                  <tr key={li.id} className={`border-l-2 transition-colors ${li.is_custom ? "border-amber-400 bg-amber-50/40 dark:bg-amber-900/10" : "border-transparent hover:border-[var(--color-primary)] hover:bg-gray-50/60 dark:hover:bg-gray-800/40"}`}>
                    <td className="px-3 py-2.5 text-gray-900 dark:text-gray-100">
                      <span className="flex flex-wrap items-center gap-1.5">
                        {li.is_custom && (
                          <Wrench size={12} className="text-amber-500 shrink-0" />
                        )}
                        <span className="font-medium">{li.line_item_type_name}</span>
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap tabular-nums">
                      {li.quantity}
                      {li.line_item_type_unit && (
                        <span className="ml-1 text-gray-400 dark:text-gray-500">{li.line_item_type_unit}</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap tabular-nums">
                      {li.unit_rate != null
                        ? `${parseFloat(li.unit_rate).toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} PLN`
                        : <span className="text-gray-300 dark:text-gray-600">—</span>}
                    </td>
                    <td className="px-3 py-2.5 text-right font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap tabular-nums">
                      {li.total_value != null
                        ? `${parseFloat(li.total_value).toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} PLN`
                        : <span className="text-gray-300 dark:text-gray-600 font-normal">—</span>}
                    </td>
                    <td className="hidden sm:table-cell px-3 py-2.5 text-gray-500 dark:text-gray-400 max-w-xs truncate">
                      {li.description || <span className="text-gray-300 dark:text-gray-600">—</span>}
                    </td>
                    <td className="hidden sm:table-cell px-3 py-2.5 text-center">
                      {li.attachment_required ? (
                        <span className="inline-flex items-center rounded-full bg-green-50 dark:bg-green-900/30 px-2 py-0.5 text-xs font-semibold text-green-700 dark:text-green-400 ring-1 ring-inset ring-green-600/20 dark:ring-green-400/20">TAK</span>
                      ) : (
                        <span className="text-gray-300 dark:text-gray-600 text-xs">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              {(() => {
                // Sum only the line values the viewer is allowed to see (rate_visibility
                // nulls total_value otherwise). No visible values → no total row.
                const values = requisition.line_items
                  .map((li) => li.total_value)
                  .filter((v): v is string => v != null);
                if (values.length === 0) return null;
                const total = values.reduce((acc, v) => acc + parseFloat(v), 0);
                return (
                  <tfoot>
                    <tr className="border-t-2 border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/40">
                      <td className="px-3 py-2.5 text-right text-xs uppercase tracking-widest font-semibold text-gray-500 dark:text-gray-400" colSpan={3}>Razem</td>
                      <td className="px-3 py-2.5 text-right font-bold text-gray-900 dark:text-gray-100 whitespace-nowrap tabular-nums">
                        {total.toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} PLN
                      </td>
                      <td className="hidden sm:table-cell" colSpan={2}></td>
                    </tr>
                  </tfoot>
                );
              })()}
            </table>
            <MinOrderAmountNotice notice={requisition.min_order_amount_notice} className="mt-3" />
            {/* Co automat umowy ramowej dopisze do zamówienia (a czego nie) — po zatwierdzeniu
                to już widać na zamówieniu, więc tylko przed nim. */}
            {requisition.status !== "approved" && (
              <RequisitionAutoLineItems
                rows={requisition.auto_line_items}
                declined={new Set(requisition.declined_auto_line_item_types ?? [])}
                className="mt-3"
              />
            )}
          </Section>

          {/* Kamienie milowe (terminy pośrednie) — przeniosą się na zamówienie przy zatwierdzaniu. */}
          {(requisition.milestones ?? []).length > 0 && (
            <Section label={`Kamienie milowe (${requisition.milestones.length})`}>
              <ul className="space-y-2">
                {requisition.milestones.map((m, i) => (
                  <li
                    key={m.id}
                    className="flex items-center gap-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-2.5 py-2 text-sm shadow-sm"
                  >
                    <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-[var(--color-primary)]/10 text-[11px] font-bold text-[var(--color-primary)]">
                      {i + 1}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-gray-800 dark:text-gray-200" title={m.label}>
                      {m.label}
                    </span>
                    <span className="inline-flex shrink-0 items-center gap-1 font-mono text-xs text-gray-500 dark:text-gray-400">
                      <CalendarClock size={13} /> {formatDate(m.due_date)}
                    </span>
                  </li>
                ))}
              </ul>
              <p className="mt-1.5 flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500">
                <Flag size={11} /> Przeniosą się na zamówienie przy zatwierdzaniu (edytowalne przez zatwierdzającego).
              </p>
            </Section>
          )}

          {/* WPW: parcels the requisition covers, each owner with the declaration package
              (oświadczenia) handed to the negotiator as the mandatory input set on approval. */}
          {(requisition.wpw_parcels ?? []).length > 0 && (
            <Section label={`Działki do pozyskania zgód (WPW) — ${requisition.wpw_parcels.length}`}>
              <div className="border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700">
                {requisition.wpw_parcels.map((p) => (
                  <div key={p.parcel_id}>
                    <div className="px-3 py-1.5 text-xs font-mono uppercase tracking-wide text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50">
                      {p.teryt}
                      {p.short_number && <span className="ml-1.5 text-gray-400 dark:text-gray-500">· {p.short_number}</span>}
                    </div>
                    {p.owners.length === 0 ? (
                      <p className="px-3 py-2 text-xs text-gray-400 dark:text-gray-500">— brak wskazanych właścicieli —</p>
                    ) : (
                      p.owners.map((o) => (
                        <div key={o.owner_id} className="px-3 py-2">
                          <p className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-1.5">
                            {o.full_name || "—"}
                          </p>
                          <AttachmentList files={o.files} />
                        </div>
                      ))
                    )}
                  </div>
                ))}
              </div>
              <p className="mt-1.5 text-xs text-gray-400 dark:text-gray-500">
                Po zatwierdzeniu zamówienie połączy się z działkami automatycznie, a powyższe komplety
                trafią do negocjatora per właściciel (może tam odkładać zgody dla każdej działki).
              </p>
            </Section>
          )}

          {/* Attachments (multifile) — hidden when empty on a WPW requisition, where the real
              attachments are the per-owner packages listed above. */}
          {(requisition.files_detail.length > 0 || (requisition.wpw_parcels ?? []).length === 0) && (
            <Section label={`Załączniki${requisition.files_detail.length ? ` (${requisition.files_detail.length})` : ""}`}>
              <AttachmentList files={requisition.files_detail} />
            </Section>
          )}

          {/* Result files of the generated order — reporter can review/accept/reject/delete. */}
          {requisition.order_id && (
            <div className="mt-6">
              <OrderResultFilesSection orderId={requisition.order_id} />
            </div>
          )}
        </div>
      )}

      {/* Edit mode */}
      {editMode && (
        <form onSubmit={handleSubmit(onSave)} className="flex flex-col gap-5">
          {/* Controlled fields (not register): SearchableSelect and late-loading option
              lists only reflect values passed via the value prop, so reset()-populated
              data would otherwise not show up */}
          <Controller
            name="project"
            control={control}
            render={({ field }) => (
              <SearchableSelect
                label="Przedsięwzięcie"
                placeholder="— wybierz —"
                options={projectOptions}
                error={errors.project?.message}
                name={field.name}
                value={field.value ?? ""}
                onBlur={field.onBlur}
                onChange={(e) => field.onChange(e.target.value)}
              />
            )}
          />
          <Controller
            name="contractor"
            control={control}
            render={({ field }) => (
              <Select
                label="Podwykonawca (opcjonalnie)"
                placeholder="— brak / do ustalenia —"
                options={contractorOptions}
                name={field.name}
                value={field.value ?? ""}
                onBlur={field.onBlur}
                onChange={(e) => {
                  const prev = field.value;
                  field.onChange(e);
                  // Only clear line items when user explicitly picks a DIFFERENT contractor
                  if (e.target.value !== prev) {
                    fields.forEach((_, i) => setValue(`line_items.${i}.line_item_type`, ""));
                  }
                }}
              />
            )}
          />
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Opis zapotrzebowania</label>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <RichTextEditor
                  value={field.value ?? ""}
                  onChange={field.onChange}
                  placeholder="Opisz szczegółowo zakres prac, wymagania lub inne istotne informacje…"
                  onImageUpload={handleImageUpload}
                />
              )}
            />
            {errors.description && (
              <p className="text-xs text-red-600">{errors.description.message}</p>
            )}
          </div>

          {requisition.files_detail.length > 0 && (
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1.5">
                <Paperclip size={14} />
                Załączniki ({requisition.files_detail.length})
              </label>
              <AttachmentList
                files={requisition.files_detail}
                onDelete={handleDeleteAttachment}
                deletingId={deletingAttachmentId}
              />
            </div>
          )}

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1.5">
              <Paperclip size={14} />
              Dodaj załączniki
            </label>
            <FileDropZone
              files={newAttachments}
              onFilesChange={(files) => {
                const tooLarge = findTooLargeFile(files);
                setUploadError(tooLarge ? fileTooLargeMessage(tooLarge) : null);
                setNewAttachments(files.filter((f) => f.size <= MANAGED_FILE_MAX_BYTES));
              }}
              multiple
            />
          </div>

          <Select
            label="Priorytet"
            options={[
              { value: "low", label: "Niski" },
              { value: "standard", label: "Standardowy" },
              { value: "high", label: "Wysoki" },
            ]}
            error={errors.priority?.message}
            {...register("priority")}
          />
          <div>
            <div className="flex flex-col gap-1">
              <label htmlFor="edit-deadline" className="text-xs font-mono uppercase tracking-wider text-[var(--color-text-muted)]">Termin realizacji (opcjonalny)</label>
              <Controller
                name="deadline"
                control={control}
                render={({ field }) => (
                  <DatePicker id="edit-deadline" value={field.value ?? ""} onChange={field.onChange} />
                )}
              />
            </div>
            {editDeadlineTooEarly && (
              <p className="mt-1 text-xs text-red-600">
                {DEADLINE_BEFORE_MILESTONE_MSG} ({editLatestMs}).
              </p>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">Pozycje realizacyjne</h3>
              <div className="flex gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={() =>
                    append({ is_custom: false, line_item_type: "", quantity: 1, description: "", attachment_required: true, required_file_extension: "", result_files_expected: false })
                  }
                >
                  <Plus size={14} /> Z umowy
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={() =>
                    append({ is_custom: true, custom_name: "", custom_unit: "", custom_unit_rate: undefined, quantity: 1, description: "", attachment_required: false, required_file_extension: "", result_files_expected: false })
                  }
                  className="border-amber-400 text-amber-700 hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-amber-900/20"
                >
                  <Wrench size={14} /> Niestandardowa
                </Button>
              </div>
            </div>
            {!contractorId && (
              <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded px-3 py-2 mb-3">
                Wybierz podwykonawcę, aby móc wskazać pozycje z umowy ramowej. Możesz też dodać pozycje niestandardowe bez wyboru podwykonawcy.
              </p>
            )}
            <div className="flex flex-col gap-3">
              {fields.map((field, index) => (
                <LineItemFormRow
                  key={field.id}
                  index={index}
                  control={control}
                  register={register}
                  errors={errors}
                  lineItemOptions={lineItemOptions}
                  contractorId={contractorId || undefined}
                  onRemove={() => remove(index)}
                  lineItemAttachmentMap={lineItemAttachmentMap}
                  setValue={setValue}
                />
              ))}
            </div>
            <MinOrderAmountNotice notice={editMinOrderNotice} className="mt-3" />
          </div>

          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
            <MilestonesEditor value={editMilestones} onChange={setEditMilestones} />
          </div>

          {uploadError && <p className="text-sm text-red-600">{uploadError}</p>}
          {updateMutation.isError && (
            <p className="text-sm text-red-600">Błąd zapisu. Sprawdź dane i spróbuj ponownie.</p>
          )}
          <div className="flex gap-3 pt-2">
            <Button type="submit" loading={updateMutation.isPending || isUploading} disabled={editDeadlineTooEarly}>
              {isUploading ? "Przesyłanie plików…" : "Zapisz zmiany"}
            </Button>
            <Button type="button" variant="secondary" onClick={() => { setEditMode(false); setNewAttachments([]); }}>Anuluj</Button>
          </div>
        </form>
      )}

      {/* History / Audit logs */}
      {!editMode && (
        <div className="mt-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2 border-l-4 border-[var(--color-primary)] pl-3">
              <Clock size={16} className="text-gray-400 dark:text-gray-500" /> Historia zmian
            </h2>
            <Button size="sm" variant="secondary" onClick={() => setNoteModal(true)}>
              + Dodaj notatkę
            </Button>
          </div>
          {(auditLogs ?? []).length === 0 ? (
            <p className="text-sm text-gray-400 dark:text-gray-500">Brak zapisów historii.</p>
          ) : (
            <div className="flex flex-col gap-2">
              {(auditLogs ?? []).map((log) => (
                <div key={log.id} className="flex gap-3 p-3 bg-gray-50 dark:bg-gray-800/50">
                  <div className="flex-1">
                    <p className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
                      {highlightMentions(log.description, (log.mentions ?? []).map((m) => m.full_name))}
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                      {log.user_name} · {new Date(log.created_at).toLocaleString("pl")}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Approve modal */}
      <Modal open={approveModal} onClose={() => setApproveModal(false)} title="Zatwierdź zapotrzebowanie" size="sm">
        <div className="flex flex-col gap-4">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Podaj termin realizacji zamówienia. Jest to wymagane do zatwierdzenia zapotrzebowania.
          </p>
          {/* Wartość poniżej kwoty minimalnej z umowy ramowej — do decyzji zatwierdzającego. */}
          <MinOrderAmountNotice notice={requisition.min_order_amount_notice} />
          {/* Pozycje dopisywane automatem nie są na liście zapotrzebowania — zatwierdzający
              musi je zobaczyć (i móc odznaczyć), zanim zobowiązanie trafi na zamówienie. */}
          <RequisitionAutoLineItems
            rows={requisition.auto_line_items}
            declined={approveDeclinedAuto}
            onToggle={(autoId) =>
              setApproveDeclinedAuto((s) => {
                const next = new Set(s);
                if (next.has(autoId)) next.delete(autoId);
                else next.add(autoId);
                return next;
              })
            }
          />
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Termin realizacji *
            </label>
            <DatePicker
              value={approveDeadline}
              onChange={setApproveDeadline}
              min={approveLatestMs ?? new Date().toISOString().split("T")[0]}
            />
            {approveDeadlineTooEarly && (
              <p className="mt-1 text-xs text-red-600">
                {DEADLINE_BEFORE_MILESTONE_MSG} ({approveLatestMs}).
              </p>
            )}
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Typ zamówienia
            </label>
            {requisition.suggested_order_type ? (
              // Raised from the DLC Działki tab → locked to the WPW type chosen there.
              <>
                <input
                  disabled
                  className="block w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 cursor-not-allowed"
                  value={
                    requisition.suggested_order_type === "wpw_terenowe"
                      ? "Pozyskanie zgód WPW (terenowe)"
                      : "Pozyskanie zgód WPW (kompleksowe, art.124)"
                  }
                />
                <p className="text-xs text-gray-500 mt-1">Ustawione automatycznie — zapotrzebowanie z trybu „Działki / zgody prywatne”.</p>
              </>
            ) : (
              <select
                className="block w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                value={approveOrderType}
                onChange={(e) => setApproveOrderType(e.target.value)}
              >
                {MANUAL_ORDER_TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            )}
          </div>
          <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
            <MilestonesEditor value={approveMilestones} onChange={setApproveMilestones} />
            <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
              Przeniosą się na zamówienie jako kamienie milowe. Możesz je teraz dowolnie zmienić.
            </p>
          </div>
          {requisition.ze_project_id && (
            <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
              <ProjectFileShareFields
                connectionId={requisition.ze_project_id}
                value={approveShare}
                onChange={setApproveShare}
              />
              <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                Udostępnia kierownik przedsięwzięcia albo dyrektor. Później to samo zrobisz
                w zakładce „Pliki” projektu ZE — tam też cofniesz udostępnienie.
              </p>
            </div>
          )}
          {approveMutation.isError && (
            <p className="text-sm text-red-600">
              {(approveMutation.error as { response?: { data?: { errors?: { detail?: string } } } })
                ?.response?.data?.errors?.detail ?? "Błąd zatwierdzenia. Spróbuj ponownie."}
            </p>
          )}
          <div className="flex gap-3">
            <Button
              loading={approveMutation.isPending}
              disabled={
                !approveDeadline || approveDeadlineTooEarly || !projectFileShareValid(approveShare)
              }
              onClick={handleApproveConfirm}
            >
              Zatwierdź
            </Button>
            <Button variant="secondary" onClick={() => setApproveModal(false)}>Anuluj</Button>
          </div>
        </div>
      </Modal>

      {/* Reject modal */}
      <RequisitionRejectModal
        requisitionId={requisition.id}
        open={rejectModal}
        onClose={() => setRejectModal(false)}
      />

      {/* Add note modal */}
      <Modal open={noteModal} onClose={() => setNoteModal(false)} title="Dodaj notatkę do historii" size="sm">
        <div className="flex flex-col gap-4">
          {/* Notatka bywa pytaniem do konkretnej osoby — @ oznacza uczestnika i wysyła
              mu dzwonek prowadzący do tego zapotrzebowania. */}
          <MentionTextarea
            value={noteText}
            mentionIds={noteMentions}
            onChange={(text, ids) => { setNoteText(text); setNoteMentions(ids); }}
            rows={4}
            placeholder="Opisz zmianę lub dodaj komentarz… (wpisz @ aby oznaczyć)"
          />
          <div className="flex gap-3">
            <Button loading={addLogMutation.isPending} onClick={handleAddNote} disabled={!noteText.trim()}>
              Zapisz notatkę
            </Button>
            <Button variant="secondary" onClick={() => setNoteModal(false)}>Anuluj</Button>
          </div>
        </div>
      </Modal>

      <Modal open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} title="Usuń zapotrzebowanie" size="sm">
        <div className="flex flex-col gap-4">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Czy na pewno chcesz usunąć to zapotrzebowanie? Tej operacji nie można cofnąć.
          </p>
          {deleteMutation.isError && (
            <p className="text-sm text-red-600">
              {(deleteMutation.error as { response?: { data?: { errors?: { detail?: string } } } })
                ?.response?.data?.errors?.detail ?? "Błąd usunięcia. Spróbuj ponownie."}
            </p>
          )}
          <div className="flex gap-3">
            <Button
              variant="danger"
              loading={deleteMutation.isPending}
              onClick={() =>
                deleteMutation.mutate(requisition.id, { onSuccess: () => navigate("/requisitions") })
              }
            >
              Usuń
            </Button>
            <Button variant="secondary" onClick={() => setDeleteConfirmOpen(false)}>
              Anuluj
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">{label}</h2>
      {children}
    </div>
  );
}

function InfoCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-white dark:bg-gray-800 p-4">
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{label}</p>
      <p className="font-medium text-gray-900 dark:text-gray-100">{value}</p>
    </div>
  );
}
